import type { ComponentProps } from 'react';
import type { PAGE_POSITIONS } from 'types';
import type { ArticleCard as ArticleCardType } from 'types/capi/shared';

import ArticleCard from '~/components/ArticleCard';

type TilesLayout = 'grid' | 'scroll';

type Props = {
  background?: string;
  items: ArticleCardType[];
  layout?: TilesLayout;
  modulePosition: number;
  moduleType: string;
  pagePosition: PAGE_POSITIONS;
};

type CardView = Pick<
  ComponentProps<typeof ArticleCard>,
  'className' | 'hideCta' | 'mdRatio' | 'options' | 'variant'
>;

type TileSpec = CardView | { desktop: CardView; mobile: CardView };

type ColumnTiles = readonly [TileSpec, TileSpec];

type Layout = {
  column: string;
  columns: readonly [ColumnTiles, ColumnTiles, ColumnTiles];
  container: string;
};

type SharedCardProps = {
  modulePosition: number;
  moduleType: string;
  name: 'LATEST_NEWS';
  pagePosition: PAGE_POSITIONS;
};

const PORTRAIT_ON_MOBILE =
  'max-md:min-w-[285px] max-md:w-[70vw] max-md:self-center';
const MOBILE_ONLY = 'md:hidden';
const DESKTOP_ONLY = 'hidden md:block';

const PORTRAIT_OVER = { compact: true, titleLines: 3 } as const;
const LANDSCAPE_OVER = { compact: true, titleLines: 2 } as const;
const LANDSCAPE_UNDER = { compact: true, titleLines: 1 } as const;
const LANDSCAPE_UNDER_WIDE = { titleLines: 1 } as const;

const PORTRAIT_TILE: CardView = {
  className: PORTRAIT_ON_MOBILE,
  options: PORTRAIT_OVER,
  variant: '4/5-text-over'
};

const NEWS_LAYOUT: Layout = {
  column: 'flex flex-col gap-6 md:flex-1',
  columns: [
    [
      PORTRAIT_TILE,
      {
        desktop: { options: LANDSCAPE_UNDER, variant: '16/9-text-under' },
        mobile: {
          hideCta: true,
          options: LANDSCAPE_OVER,
          variant: '16/9-text-over'
        }
      }
    ],
    [
      {
        desktop: {
          hideCta: true,
          options: LANDSCAPE_UNDER,
          variant: '16/9-text-under'
        },
        mobile: PORTRAIT_TILE
      },
      {
        hideCta: true,
        mdRatio: '4:5',
        options: LANDSCAPE_OVER,
        variant: '16/9-text-over'
      }
    ],
    [
      PORTRAIT_TILE,
      {
        desktop: {
          hideCta: true,
          options: LANDSCAPE_UNDER,
          variant: '16/9-text-under'
        },
        mobile: {
          hideCta: true,
          options: LANDSCAPE_OVER,
          variant: '16/9-text-over'
        }
      }
    ]
  ],
  container: 'content-width flex flex-col gap-6 py-10 md:flex-row md:py-20'
};

const HOMEPAGE_LAYOUT: Layout = {
  column: 'flex flex-1 flex-col gap-6',
  columns: [
    [
      { variant: '4/5-text-over' },
      { options: LANDSCAPE_UNDER_WIDE, variant: '16/9-text-under' }
    ],
    [
      { options: LANDSCAPE_UNDER_WIDE, variant: '16/9-text-under' },
      { variant: '4/5-text-over' }
    ],
    [
      { variant: '4/5-text-over' },
      { options: LANDSCAPE_UNDER_WIDE, variant: '16/9-text-under' }
    ]
  ],
  container:
    'content-width no-scrollbar mx-auto flex gap-6 overflow-x-scroll py-10 md:py-20'
};

const LAYOUTS: Record<TilesLayout, Layout> = {
  grid: NEWS_LAYOUT,
  scroll: HOMEPAGE_LAYOUT
};

export default function Tiles({
  background = 'bg-grey-tile',
  items,
  layout = 'scroll',
  modulePosition,
  moduleType,
  pagePosition
}: Props) {
  const [card1, card2, card3, card4, card5, card6] = items;
  if (!card1 || !card2 || !card3 || !card4 || !card5 || !card6) return null;

  const { column, columns, container } = LAYOUTS[layout];
  const shared: SharedCardProps = {
    modulePosition,
    moduleType,
    name: 'LATEST_NEWS',
    pagePosition
  };

  return (
    <div className={`w-full ${background}`}>
      <div className={container}>
        <Column
          cards={[card1, card2]}
          className={column}
          shared={shared}
          startPosition={1}
          tiles={columns[0]}
        />
        <Column
          cards={[card3, card4]}
          className={column}
          shared={shared}
          startPosition={3}
          tiles={columns[1]}
        />
        <Column
          cards={[card5, card6]}
          className={column}
          shared={shared}
          startPosition={5}
          tiles={columns[2]}
        />
      </div>
    </div>
  );
}

function Column({
  cards,
  className,
  shared,
  startPosition,
  tiles
}: {
  cards: readonly [ArticleCardType, ArticleCardType];
  className: string;
  shared: SharedCardProps;
  startPosition: number;
  tiles: ColumnTiles;
}) {
  const [topCard, bottomCard] = cards;
  const [topTile, bottomTile] = tiles;

  return (
    <div className={className}>
      <Tile
        card={topCard}
        position={startPosition}
        shared={shared}
        tile={topTile}
      />
      <Tile
        card={bottomCard}
        position={startPosition + 1}
        shared={shared}
        tile={bottomTile}
      />
    </div>
  );
}

function Tile({
  card,
  position,
  shared,
  tile
}: {
  card: ArticleCardType;
  position: number;
  shared: SharedCardProps;
  tile: TileSpec;
}) {
  if ('variant' in tile) {
    return (
      <ArticleCard {...shared} {...tile} item={card} position={position} />
    );
  }

  return (
    <>
      <ArticleCard
        {...shared}
        {...tile.mobile}
        className={withVisibility(tile.mobile.className, MOBILE_ONLY)}
        item={card}
        position={position}
      />
      <ArticleCard
        {...shared}
        {...tile.desktop}
        className={withVisibility(tile.desktop.className, DESKTOP_ONLY)}
        item={card}
        position={position}
      />
    </>
  );
}

function withVisibility(className: string | undefined, visibility: string) {
  return className ? `${className} ${visibility}` : visibility;
}
